import { ReactNode, useState } from 'react';

import { Box } from '../../common/ui/Box';
import { Checkbox } from '../Checkbox/Checkbox';
import { CheckboxGroup, CheckboxGroupOption } from '../CheckboxGroup/CheckboxGroup';
import { Collapsable } from '../Collapsable/Collapsable';
import { IconButton } from '../IconButton/IconButton';

import { CheckboxWrapper } from './components/CheckboxWrapper';
import { Divider } from './components/Divider';
import { RotatingChevron } from './components/RotatingChevron';
import { SectionHeader } from './components/SectionHeader';

export interface CollapsableCheckboxGroupSection<Value extends string = string> {
    id: string;
    label: ReactNode;
    options: CheckboxGroupOption<Value>[];
    disabled?: boolean;
    defaultExpanded?: boolean;
}

const getInitialExpandedSectionIds = <Value extends string>(
    sections: CollapsableCheckboxGroupSection<Value>[],
    defaultExpandedSectionIds: string[] | undefined
) => {
    if (defaultExpandedSectionIds !== undefined) {
        return defaultExpandedSectionIds;
    }

    return sections.filter((section) => section.defaultExpanded).map((section) => section.id);
};

export interface CollapsableCheckboxGroupProps<Value extends string = string> {
    sections: CollapsableCheckboxGroupSection<Value>[];
    value: Value[];
    onChange: (value: Value[]) => void;
    defaultExpandedSectionIds?: string[];
    columns?: 1 | 2;
    truncateLabels?: boolean;
}

export const CollapsableCheckboxGroup = <Value extends string = string>({
    sections,
    value,
    onChange,
    defaultExpandedSectionIds,
    columns = 2,
    truncateLabels = true,
}: CollapsableCheckboxGroupProps<Value>) => {
    const [expandedSectionIds, setExpandedSectionIds] = useState<string[]>(() =>
        getInitialExpandedSectionIds(sections, defaultExpandedSectionIds)
    );
    const selectedValues = new Set(value);
    const visibleSections = sections.filter((section) => section.options.length > 0);

    const setSectionExpanded = (sectionId: string, isExpanded: boolean) => {
        setExpandedSectionIds((currentExpandedSectionIds) => {
            if (isExpanded) {
                return currentExpandedSectionIds.includes(sectionId)
                    ? currentExpandedSectionIds
                    : [...currentExpandedSectionIds, sectionId];
            }

            return currentExpandedSectionIds.filter((expandedSectionId) => expandedSectionId !== sectionId);
        });
    };

    const toggleSection = (sectionId: string) => {
        setExpandedSectionIds((currentExpandedSectionIds) =>
            currentExpandedSectionIds.includes(sectionId)
                ? currentExpandedSectionIds.filter((expandedSectionId) => expandedSectionId !== sectionId)
                : [...currentExpandedSectionIds, sectionId]
        );
    };

    const handleSectionSelectionChange = (section: CollapsableCheckboxGroupSection<Value>) => {
        const enabledOptions = section.options.filter((option) => !option.disabled);
        const isEveryEnabledOptionSelected = enabledOptions.every((option) => selectedValues.has(option.value));
        const enabledOptionValues = new Set(enabledOptions.map((option) => option.value));

        setSectionExpanded(section.id, true);

        if (isEveryEnabledOptionSelected) {
            onChange(value.filter((selectedValue) => !enabledOptionValues.has(selectedValue)));
            return;
        }

        const nextValue = [...value];
        enabledOptions.forEach((option) => {
            if (!selectedValues.has(option.value)) {
                nextValue.push(option.value);
            }
        });
        onChange(nextValue);
    };

    return (
        <Box display="flex" flexDirection="column" gap={12} width="100%">
            {visibleSections.map((section) => {
                const isExpanded = expandedSectionIds.includes(section.id);
                const enabledOptions = section.options.filter((option) => !option.disabled);
                const enabledSelectedCount = enabledOptions.filter((option) => selectedValues.has(option.value)).length;
                const isSectionCheckboxDisabled = section.disabled || enabledOptions.length === 0;
                const isEveryEnabledOptionSelected =
                    enabledOptions.length > 0 && enabledSelectedCount === enabledOptions.length;
                const isSectionIndeterminate =
                    enabledSelectedCount > 0 &&
                    enabledSelectedCount < enabledOptions.length &&
                    !isSectionCheckboxDisabled;

                return (
                    <Box key={section.id} minWidth={0} width="100%">
                        <Collapsable
                            isOpen={isExpanded}
                            header={
                                <>
                                    <SectionHeader
                                        data-testid={`collapsable-checkbox-group-${section.id}-header`}
                                        onClick={() => toggleSection(section.id)}
                                    >
                                        <CheckboxWrapper onClick={(event) => event.stopPropagation()}>
                                            <Checkbox
                                                checked={isEveryEnabledOptionSelected}
                                                disabled={isSectionCheckboxDisabled}
                                                indeterminate={isSectionIndeterminate}
                                                label={section.label}
                                                onChange={() => handleSectionSelectionChange(section)}
                                                truncateLabel={truncateLabels}
                                            />
                                        </CheckboxWrapper>
                                        <IconButton
                                            data-testid={`collapsable-checkbox-group-${section.id}-toggle`}
                                            type="button"
                                            Icon={(props) => <RotatingChevron {...props} $isExpanded={isExpanded} />}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                toggleSection(section.id);
                                            }}
                                        />
                                    </SectionHeader>
                                    <Divider />
                                </>
                            }
                        >
                            <Box paddingTop={8} paddingX={4} paddingBottom={4}>
                                <CheckboxGroup
                                    options={section.options}
                                    value={value}
                                    onChange={onChange}
                                    disabled={section.disabled}
                                    columns={columns}
                                    truncateLabels={truncateLabels}
                                />
                            </Box>
                        </Collapsable>
                    </Box>
                );
            })}
        </Box>
    );
};
